/**
 * Entity Configuration Utility
 * Handles loading and merging of global and tenant-specific entity configurations
 */

/**
 * Cache for entity configurations
 */
const configCache = new Map();

/**
 * In-flight requests keyed by cache key
 */
const configInFlight = new Map();

/**
 * Cache for entity metadata listings
 */
const entityMetadataCache = {
  data: null,
  promise: null,
  fetchedAt: 0
};

/**
 * Load entity configuration from backend
 * @param {string} entityName - Name of the entity (e.g., 'customer', 'lead', 'activity')
 * @param {string} tenantCode - Tenant code (optional)
 * @param {{ signal?: AbortSignal }} options - Optional; pass signal to cancel when user navigates away
 * @returns {Promise<object>} Merged entity configuration
 */
export async function loadEntityConfig(entityName, tenantCode = null, options = {}) {
  const cacheKey = `${entityName}_${tenantCode || 'global'}`;
  
  // Check cache first
  if (configCache.has(cacheKey)) {
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ Cached: ${entityName}`);
    }
    return configCache.get(cacheKey);
  }

  // Dedupe concurrent requests for the same entity/tenant
  if (configInFlight.has(cacheKey)) {
    return configInFlight.get(cacheKey);
  }

  const fetchPromise = (async () => {
    try {
      let config = null;

      // If tenant code is provided, use tenant-entity-config endpoint (already merged on backend)
      if (tenantCode) {
        try {
          if (process.env.NODE_ENV === 'development') {
            console.log(`🔍 Loading config: ${entityName}`);
          }
          const tenantResponse = await fetch(`/api/tenant-entity-config/${entityName}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'X-Tenant-Code': tenantCode
            }
          });

          if (tenantResponse.ok) {
            const tenantData = await tenantResponse.json();
            // Backend returns { success, entityName, tenantCode, config: { ...mergedConfig, hiddenFields: [...] }, hasOverrides }
            config = tenantData.config || tenantData;
            
            // Ensure hiddenFields is at the root level of config for easy access
            if (process.env.NODE_ENV === 'development') {
              console.log(`✅ Loaded: ${entityName}`);
            }
          } else {
            console.warn(`Failed to load tenant config for ${entityName}, falling back to global`);
          }
        } catch (err) {
          console.warn(`Error loading tenant config for ${entityName}:`, err);
        }
      }

      // If no tenant config loaded, fall back to global config
      if (!config) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`🔍 Loading global: ${entityName}`);
        }
        const globalResponse = await fetch(`/api/entity-config/${entityName}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (!globalResponse.ok) {
          console.warn(`Failed to load global config for ${entityName}`);
          return null;
        }

        const globalData = await globalResponse.json();
        config = globalData.entity || globalData; // Handle wrapped response
        if (process.env.NODE_ENV === 'development') {
          console.log(`✅ Loaded global: ${entityName}`);
        }
      }

      // Cache the config
      configCache.set(cacheKey, config);
      return config;
    } catch (error) {
      if (error?.name === 'AbortError') return null;
      console.error(`Error loading entity config for ${entityName}:`, error);
      return null;
    } finally {
      configInFlight.delete(cacheKey);
    }
  })();

  configInFlight.set(cacheKey, fetchPromise);
  return fetchPromise;
}

/**
 * Get metadata for all entities available in the system (admin only endpoint)
 * @param {object} options
 * @param {boolean} options.forceRefresh - Whether to bypass the cache and fetch fresh data
 * @returns {Promise<Array<{ name: string, code: string, displayName: string, displayNamePlural: string, icon?: string }>>}
 */
export async function getAllEntityMetadata({ forceRefresh = false } = {}) {
  if (!forceRefresh && entityMetadataCache.data) {
    return entityMetadataCache.data;
  }

  if (!forceRefresh && entityMetadataCache.promise) {
    return entityMetadataCache.promise;
  }

  const token = localStorage.getItem('token');
  if (!token) {
    console.warn('⚠️ No auth token found while loading entity metadata. Returning empty list.');
    entityMetadataCache.data = [];
    return entityMetadataCache.data;
  }

  const abortController = new AbortController();
  const timeoutId = setTimeout(() => abortController.abort(), 15000);

  const fetchPromise = fetch('/api/entity-config', {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    },
    signal: abortController.signal
  })
    .then(async (response) => {
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text().catch(() => response.statusText);
        throw new Error(`Failed to load entity metadata: ${response.status} ${errorText}`);
      }

      const payload = await response.json();
      const entities = Array.isArray(payload.entities) ? payload.entities : [];

      const normalized = entities.map((item) => {
        const metadata = item.metadata || {};
        const name = metadata.name || item.entityName || '';
        const displayName = metadata.displayName || name;
        const displayNamePlural = metadata.displayNamePlural || metadata.displayName || name;

        return {
          name,
          code: item.entityName || name?.toLowerCase?.() || '',
          displayName,
          displayNamePlural,
          icon: metadata.icon || null,
          raw: item
        };
      }).filter(entity => entity.name);

      entityMetadataCache.data = normalized;
      entityMetadataCache.promise = null;
      entityMetadataCache.fetchedAt = Date.now();

      return normalized;
    })
    .catch((error) => {
      clearTimeout(timeoutId);
      entityMetadataCache.promise = null;
      console.error('❌ Error fetching entity metadata:', error);
      throw error;
    });

  entityMetadataCache.promise = fetchPromise;
  return fetchPromise;
}

/**
 * Merge tenant-specific configuration with global configuration
 * Tenant-specific settings override global settings
 * @param {object} globalConfig - Global entity configuration
 * @param {object} tenantConfig - Tenant-specific configuration
 * @returns {object} Merged configuration
 */
export function mergeConfigs(globalConfig, tenantConfig) {
  if (!tenantConfig) return globalConfig;
  if (!globalConfig) return tenantConfig;

  const merged = JSON.parse(JSON.stringify(globalConfig)); // Deep clone

  // Merge fields
  if (tenantConfig.fields) {
    merged.fields = merged.fields || {};
    
    Object.keys(tenantConfig.fields).forEach(fieldName => {
      const tenantField = tenantConfig.fields[fieldName];
      
      if (merged.fields[fieldName]) {
        // Merge with existing field
        merged.fields[fieldName] = {
          ...merged.fields[fieldName],
          ...tenantField,
          ui: {
            ...merged.fields[fieldName].ui,
            ...tenantField.ui
          },
          validation: {
            ...merged.fields[fieldName].validation,
            ...tenantField.validation
          }
        };
      } else {
        // Add new tenant-specific field
        merged.fields[fieldName] = tenantField;
      }
    });
  }

  // Merge views
  if (tenantConfig.views) {
    merged.views = merged.views || {};
    Object.keys(tenantConfig.views).forEach(viewName => {
      if (merged.views[viewName]) {
        merged.views[viewName] = {
          ...merged.views[viewName],
          ...tenantConfig.views[viewName]
        };
      } else {
        merged.views[viewName] = tenantConfig.views[viewName];
      }
    });
  }

  // Merge other top-level properties
  Object.keys(tenantConfig).forEach(key => {
    if (key !== 'fields' && key !== 'views') {
      merged[key] = {
        ...merged[key],
        ...tenantConfig[key]
      };
    }
  });

  return merged;
}

/**
 * Get fields for list view based on entity configuration
 * @param {object} config - Entity configuration
 * @returns {array} Array of field definitions for list view
 */
export function getListViewFields(config, options = {}) {
  if (!config || !config.fields) return [];

  const { tabName = null } = options;
  const listView = config.views?.list || {};
  let fields = [];

  const normaliseFieldEntry = (fieldName, index, overrides = {}) => {
    if (!fieldName || !config.fields[fieldName]) return null;
    const baseField = config.fields[fieldName];
    
    // Check if field is hidden
    if (isFieldHidden(config, fieldName, baseField)) {
      return null;
    }
    
    const mergedField = {
      name: fieldName,
      ...baseField,
      order: overrides.order ?? baseField.ui?.order ?? index ?? 999
    };

    if (overrides.label) {
      mergedField.displayName = overrides.label;
    } else if (overrides.displayName) {
      mergedField.displayName = overrides.displayName;
    }

    if (overrides.ui) {
      mergedField.ui = {
        ...(baseField.ui || {}),
        ...overrides.ui
      };
    }

    if (overrides.validation) {
      mergedField.validation = {
        ...(baseField.validation || {}),
        ...overrides.validation
      };
    }

    return mergedField;
  };

  // Check for tab-specific layout
  if (tabName && Array.isArray(listView.tabs)) {
    const tabConfig = listView.tabs.find(tab => tab.id === tabName || tab.name === tabName);
    if (tabConfig) {
      const tabFields = Array.isArray(tabConfig.fields) ? tabConfig.fields : [];
      fields = tabFields
        .map((entry, index) => {
          if (typeof entry === 'string') {
            return normaliseFieldEntry(entry, index);
          }
          if (entry && typeof entry === 'object') {
            const fieldName = entry.field || entry.name;
            return normaliseFieldEntry(fieldName, index, entry);
          }
          return null;
        })
        .filter(Boolean);
    }
  }

  // Check for explicit views.list.fields array (from backend merge)
  if (fields.length === 0 && Array.isArray(listView.fields)) {
    fields = listView.fields
      .map((fieldName, index) => normaliseFieldEntry(fieldName, index))
      .filter(Boolean);
  }

  // Fallback: use fields marked showInList, sorted by order
  if (fields.length === 0) {
    fields = Object.keys(config.fields)
      .map(fieldName => {
        const field = config.fields[fieldName];
        if (!field?.ui?.showInList) return null;
        // Check if field is hidden
        if (isFieldHidden(config, fieldName, field)) return null;
        return {
          name: fieldName,
          ...field,
          order: field.ui.order || 999
        };
      })
      .filter(Boolean);
  }

  // Sort by order property
  fields.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));

  return fields;
}

/**
 * Get configured list view tabs
 * @param {object} config - Entity configuration
 * @returns {array} Array of tab definitions { id, label, description, filters, criteria, fields, order, default }
 */
export function getListViewTabs(config) {
  const listView = config?.views?.list;
  if (!listView || !Array.isArray(listView.tabs)) return [];

  return listView.tabs
    .map((tab, index) => {
      const id = tab.id || tab.name || `tab_${index}`;
      if (!id) return null;

      const filters = tab.filters && typeof tab.filters === 'object' ? tab.filters : {};
      const criteria = tab.criteria && typeof tab.criteria === 'object' ? tab.criteria : null;
      const fields = Array.isArray(tab.fields) ? tab.fields : [];

      return {
        id,
        label: tab.label || tab.displayName || tab.name || id,
        description: tab.description || '',
        icon: tab.icon || null,
        filters,
        criteria,
        fields,
        order: tab.order ?? index,
        default: listView.defaultTab
          ? (tab.id && tab.id === listView.defaultTab) || (tab.name && tab.name === listView.defaultTab)
          : index === 0
      };
    })
    .filter(Boolean)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

/**
 * Get fields for detail view based on entity configuration
 * @param {object} config - Entity configuration
 * @returns {array} Array of field definitions for detail view
 */
export function getDetailViewFields(config) {
  if (!config || !config.fields) return [];

  const fields = [];
  
  // Get fields marked for detail view
  Object.keys(config.fields).forEach(fieldName => {
    const field = config.fields[fieldName];
    // Check if field is hidden
    if (isFieldHidden(config, fieldName, field)) return;
    if (field.ui && field.ui.showInDetail) {
      fields.push({
        name: fieldName,
        ...field,
        order: field.ui.order || 999,
        section: field.group || 'General'
      });
    }
  });

  // Sort by order
  fields.sort((a, b) => a.order - b.order);

  return fields;
}

/**
 * Get fields for form view based on entity configuration
 * @param {object} config - Entity configuration
 * @returns {array} Array of field definitions for form view
 */
export function getFormViewFields(config) {
  if (!config || !config.fields) return [];

  const fields = [];
  
  // Get fields marked for form view
  Object.keys(config.fields).forEach(fieldName => {
    const field = config.fields[fieldName];
    // Check if field is hidden
    if (isFieldHidden(config, fieldName, field)) return;
    if (field.ui && field.ui.showInForm) {
      fields.push({
        name: fieldName,
        ...field,
        order: field.ui.order || 999,
        section: field.group || 'General'
      });
    }
  });

  // Sort by order
  fields.sort((a, b) => a.order - b.order);

  return fields;
}

/**
 * Check if a field is hidden (via _tenantHidden flag or hiddenFields array)
 * @param {object} config - Entity configuration
 * @param {string} fieldName - Name of the field
 * @param {object} field - Field configuration object
 * @returns {boolean} Whether the field is hidden
 */
export function isFieldHidden(config, fieldName, field) {
  if (!config || !field) return false;
  
  // Check for _tenantHidden flag (set by backend when field is in hiddenFields)
  if (field._tenantHidden === true) {
    return true;
  }
  
  // Check if field is in hiddenFields array (at config root or entity level)
  const hiddenFields = config.hiddenFields || config.entity?.hiddenFields || [];
  if (Array.isArray(hiddenFields) && hiddenFields.includes(fieldName)) {
    return true;
  }
  
  return false;
}

/**
 * Check if a field should be visible based on conditional visibility rules
 * @param {object} field - Field configuration
 * @param {object} data - Current data object
 * @returns {boolean} Whether the field should be visible
 */
export function isFieldVisible(field, data) {
  if (!field.ui || !field.ui.visibleWhen) return true;

  const condition = field.ui.visibleWhen;
  
  // Parse simple conditions like "CustomerType = 'Person'"
  const match = condition.match(/(\w+)\s*=\s*'([^']+)'/);
  if (match) {
    const [, fieldName, value] = match;
    return data[fieldName] === value;
  }

  return true;
}

/**
 * Get field display value
 * @param {object} field - Field configuration
 * @param {any} value - Raw field value
 * @param {object} data - Full data object (for lookups)
 * @returns {string} Formatted display value
 */
export function getFieldDisplayValue(field, value, data = {}) {
  if (value === null || value === undefined || value === '') return '-';

  // Handle lookup fields - if field has lookup config and data contains name field, use it
  if (field.lookup && field.lookup.enabled) {
    const fieldName = field.name || '';
    
    // Common patterns for name fields (e.g., SalespersonID -> SalespersonName)
    // Check for exact match first (e.g., SalespersonID -> SalespersonName)
    const nameFieldPatterns = [
      fieldName.replace(/ID$/, 'Name'), // SalespersonID -> SalespersonName
      fieldName.replace(/Id$/, 'Name'),  // SalespersonId -> SalespersonName
      fieldName.replace(/ID$/, 'FirstName') + ' ' + fieldName.replace(/ID$/, 'LastName'), // For separate first/last
    ];
    
    // Check if data contains a name field
    for (const pattern of nameFieldPatterns) {
      if (data[pattern] && data[pattern] !== null && data[pattern] !== '') {
        return data[pattern];
      }
    }
    
    // Check for separate FirstName/LastName fields
    const firstNameField = fieldName.replace(/ID$/, 'FirstName') || fieldName.replace(/Id$/, 'FirstName');
    const lastNameField = fieldName.replace(/ID$/, 'LastName') || fieldName.replace(/Id$/, 'LastName');
    if (data[firstNameField] || data[lastNameField]) {
      const firstName = data[firstNameField] || '';
      const lastName = data[lastNameField] || '';
      const fullName = `${firstName} ${lastName}`.trim();
      if (fullName) return fullName;
    }
    
    // If lookup has displayFields, try to construct from those
    if (field.lookup.displayFields && Array.isArray(field.lookup.displayFields)) {
      const displayParts = field.lookup.displayFields
        .map(df => {
          // Check for field name pattern (e.g., SalespersonFirstName, SalespersonLastName)
          const displayFieldName = fieldName.replace(/ID$/, df) || fieldName.replace(/Id$/, df);
          return data[displayFieldName];
        })
        .filter(Boolean);
      
      if (displayParts.length > 0) {
        return displayParts.join(' ');
      }
    }
  }

  // Handle different field types
  switch (field.dataType) {
    case 'boolean':
      return value ? 'Yes' : 'No';
    
    case 'date':
    case 'datetime':
      return new Date(value).toLocaleDateString();
    
    case 'string':
      // Handle enum/select fields with badges
      if (field.ui && field.ui.badge && field.ui.badge[value]) {
        return value; // Return raw value, badge styling handled by UI
      }
      return value;
    
    case 'integer':
    case 'decimal':
      if (field.ui && field.ui.inputType === 'currency') {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: data.Currency || 'USD'
        }).format(value);
      }
      return value.toString();
    
    default:
      return value.toString();
  }
}

/**
 * Get badge configuration for a field value
 * @param {object} field - Field configuration
 * @param {any} value - Field value
 * @returns {object|null} Badge configuration { color, label } or null
 */
export function getFieldBadge(field, value) {
  if (!field.ui || !field.ui.badge || !field.ui.badge[value]) {
    return null;
  }

  return field.ui.badge[value];
}

/**
 * Group fields by section
 * @param {array} fields - Array of field definitions
 * @returns {object} Fields grouped by section
 */
export function groupFieldsBySection(fields) {
  const grouped = {};
  
  fields.forEach(field => {
    const section = field.section || field.group || 'General';
    if (!grouped[section]) {
      grouped[section] = [];
    }
    grouped[section].push(field);
  });

  return grouped;
}

/**
 * Clear entity config cache
 * @param {string} entityName - Optional entity name to clear specific cache
 */
export function clearConfigCache(entityName = null) {
  if (entityName) {
    // Clear all cache entries for this entity
    for (const key of configCache.keys()) {
      if (key.startsWith(entityName + '_')) {
        configCache.delete(key);
      }
    }
  } else {
    // Clear entire cache
    configCache.clear();
  }
}

export default {
  loadEntityConfig,
  getAllEntityMetadata,
  mergeConfigs,
  getListViewFields,
  getListViewTabs,
  getDetailViewFields,
  getFormViewFields,
  isFieldVisible,
  getFieldDisplayValue,
  getFieldBadge,
  groupFieldsBySection,
  clearConfigCache
};

